import React from "react";
import { GraduationCap, Sprout, Users, Sparkles, Shield, Wrench } from "lucide-react";
import PageHero from "@/components/PageHero";
import SectionLabel from "@/components/SectionLabel";
import FormWithWhatsApp from "@/components/FormWithWhatsApp";
import { buildWaLink } from "@/lib/whatsapp";

const SESSIONS = [
  {
    id: "vet-cmt",
    title: "Veterinary CMT",
    body: "Continuing Medical Training sessions for veterinarians — practical, field-relevant, and supported by Biodeal's technical team.",
    Icon: GraduationCap,
  },
  {
    id: "agrovet",
    title: "Agrovet Product Training",
    body: "Stock the right products and serve farmers better — product education, dosing clarity and field-use tips for agrovets.",
    Icon: Users,
  },
  {
    id: "farmer",
    title: "Farmer Education Sessions",
    body: "Practical animal health education for poultry, dairy and livestock farmers — accessible, hands-on and locally relevant.",
    Icon: Sprout,
  },
  {
    id: "demos",
    title: "Product Demonstration Days",
    body: "Live demonstrations of products, instruments and cold-chain handling at clinics, agrovets, farms and institutions.",
    Icon: Sparkles,
  },
  {
    id: "biosecurity",
    title: "Biosecurity Training",
    body: "Footbath protocols, fumigation, clinic and farm biosecurity — designed for real East African operating conditions.",
    Icon: Shield,
  },
  {
    id: "instruments",
    title: "Instrument-Use Demonstrations",
    body: "Burdizzo, balling guns, hoof tools and calving instruments — proper handling and field practice.",
    Icon: Wrench,
  },
];

export default function Training() {
  return (
    <div data-testid="training-page">
      <PageHero
        testId="training-hero"
        eyebrow="Training & CMT"
        title="Training, CMT and field support."
        lede="Biodeal Animal Health supports veterinary professionals, agrovets and farmers through practical training, product education and field engagement."
        image="https://images.pexels.com/photos/6235241/pexels-photo-6235241.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1100&w=1600"
        primaryCta={{ label: "Book Training Below", href: "#training-form" }}
        secondaryCta={{
          label: "WhatsApp the Training Team",
          href: buildWaLink("Hello Biodeal Animal Health, I would like to book or join a CMT / training session."),
          external: true,
        }}
      />

      {/* Session types */}
      <section className="py-20 md:py-28 bg-bah-paper">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <SectionLabel>Sessions we run</SectionLabel>
            <h2 className="mt-4 font-display text-3xl md:text-5xl text-bah-ink leading-tight">
              Six training tracks. One commitment to professional practice.
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {SESSIONS.map((s) => (
              <div
                key={s.id}
                id={s.id}
                className="card-rise bg-white border border-bah-line rounded-2xl p-6 scroll-mt-28"
                data-testid={`training-session-${s.id}`}
              >
                <div className="h-11 w-11 rounded-xl bg-bah-yellow/30 text-bah-ink grid place-content-center">
                  <s.Icon className="h-5 w-5" />
                </div>
                <h3 className="mt-5 font-display text-xl text-bah-ink">{s.title}</h3>
                <p className="mt-2 text-sm text-bah-mute leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form */}
      <section
        id="training-form"
        className="py-20 md:py-28 bg-bah-paperAlt scroll-mt-28"
        data-testid="training-form-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <SectionLabel>Request a session</SectionLabel>
            <h2 className="mt-4 font-display text-3xl md:text-4xl text-bah-ink leading-tight">
              Book CMT, training, agrovet education or a farmer field day.
            </h2>
            <p className="mt-4 text-bah-mute">
              Tell us who, where and what. The Biodeal Animal Health training
              team will respond with available dates and content options.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-bah-ink">
              <li className="flex items-start gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-bah-orange mt-2" /> CMT for veterinarians
              </li>
              <li className="flex items-start gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-bah-orange mt-2" /> Agrovet product training
              </li>
              <li className="flex items-start gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-bah-orange mt-2" /> Farmer education days
              </li>
              <li className="flex items-start gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-bah-orange mt-2" /> Product / instrument demonstrations
              </li>
            </ul>
          </div>
          <div className="lg:col-span-7">
            <FormWithWhatsApp
              endpoint="/forms/training"
              testidPrefix="training"
              submitLabel="Submit Training Request"
              successText="We've received your training request. The Biodeal Animal Health training team will be in touch."
              waMessageBuilder={(v) =>
                `Hello Biodeal Animal Health, training request — ${v.name || "[name]"}, ${
                  v.role || "[role]"
                } from ${v.organization || "[organization]"} in ${v.county || "[county]"}. Training type: ${
                  v.training_type || "[type]"
                }. ${v.message || ""}`
              }
              fields={[
                { name: "name", label: "Name", required: true, placeholder: "Full name" },
                { name: "organization", label: "Organisation", placeholder: "Clinic, agrovet, school, NGO…" },
                {
                  name: "role",
                  label: "Role",
                  required: true,
                  options: ["Vet", "Agrovet", "Farmer", "Distributor", "Institution", "Other"],
                },
                { name: "county", label: "County / Location", required: true, placeholder: "e.g. Nakuru" },
                { name: "phone", label: "Phone / WhatsApp", required: true, placeholder: "+254…" },
                { name: "email", label: "Email", type: "email" },
                {
                  name: "training_type",
                  label: "Training type",
                  required: true,
                  options: [
                    "Veterinary CMT",
                    "Agrovet Product Training",
                    "Farmer Education Session",
                    "Product Demonstration Day",
                    "Biosecurity Training",
                    "Instrument-Use Demonstration",
                  ],
                },
                {
                  name: "message",
                  label: "Message",
                  textarea: true,
                  rows: 4,
                  full: true,
                  placeholder: "Number of attendees, preferred dates, topics of interest…",
                },
              ]}
            />
          </div>
        </div>
      </section>
    </div>
  );
}
